/**
 * The master screens that exist, as URL keys. Dependency-free on purpose: both the permission
 * vocabulary (which must not pull in zod) and the web registry read this list, and the API derives
 * each master's four permission codes from it.
 *
 * Adding a key here mints four permission codes. Those codes are only legal once a route enforces
 * them, which `scripts/rbac-scan.ts` checks — so a master and its permissions ship together.
 */
export const MASTER_KEYS = [
  "facilities",
  "crop-categories",
  "crops",
  "classifications",
  "varieties",
  "sp-decode-map",
  "vendors",
  "year-codes",
  "loss-types",
  "processing-resources",
  // Operator Master (user, 2026-10-27): machine operators per plant, on the Processing or Packing team.
  "operators",
  "treatment-schemas",
  "production-personnel",
  // Copied on every arrival's Unloading Sheet e-mail while Active (user, 2026-10-16).
  "email-cc",
  // Packaging (docs/designs/packaging-module.md P1): pouch / window box / master pack layout per crop × UPS.
  "pack-layouts",
  // Packaging NST (D22): master pack types (carton / bag) and their tare, per plant.
  "mp-types",
  // Stores (stores.ts): the consumables store's own masters. Its vendors stay in `vendors`.
  "stores-uoms",
  "stores-classifications",
  "stores-items",
  "stores-parties",
  // QC Analysts (user, 2026-10-23): the lab's named staff per plant; results record who tested.
  "qc-analysts",
  // Orders and Dispatch (user, 2026-10-30): the parties orders are booked for and dispatched to.
  "customers",
] as const;

/** The Stores module's own masters, in its menu's order. */
export const STORES_MASTER_KEYS = [
  "stores-classifications",
  "stores-items",
  "stores-uoms",
  "stores-parties",
] as const satisfies readonly (typeof MASTER_KEYS)[number][];

export type MasterKey = (typeof MASTER_KEYS)[number];
export const isMasterKey = (k: string): k is MasterKey =>
  (MASTER_KEYS as readonly string[]).includes(k);
